import { MENU_TAGS, type MenuTagValue } from './menu-tags'

/** 화면에 보이는 짧은 말 (4개 언어) */
export interface TechniqueNote {
  ko: string
  en: string
  ja: string
  zh: string
}

/** 기법 하나 = 메뉴 꼬리표 technique 값 + 셰프용 설명 */
export interface MenuTechnique extends MenuTagValue {
  note: TechniqueNote
}

/**
 * 기법 7개의 셰프용 설명 (2026-11-01 대표 명세 "카테고리 보강" 1장 표의 오른쪽 칸). 꼬리표 화면에서 이름 아래 한 줄로 보인다.
 * - ko 는 명세 문구 그대로. 컨핏만 '저온 숙성' 앞에 '조리·' 를 넣었다 (컨핏은 지방 속에서 익히고 두는 기법이라)
 * - 생성 프롬프트는 이 말이 아니라 menu-tags.ts 의 hint(한국어, 더 길다)를 쓴다. hint 첫 문장이 같은 뜻으로 시작한다
 * - 키는 MENU_TAGS.technique 의 id (저장값이라 바꾸지 않는다)
 */
export const MENU_TECHNIQUE_NOTES: Record<string, TechniqueNote> = {
  'sous-vide': {
    ko: '진공 포장 후 정밀 온도 제어. 단백질 수율·식감 통제용',
    en: 'Vacuum-sealed, precise temperature control for protein yield and texture',
    ja: '真空パック後に精密な温度管理。たんぱく質の歩留まりと食感をコントロール',
    zh: '真空包装后精准控温，掌控蛋白质出品率与口感',
  },
  confit: {
    ko: '오일 또는 지방에 저온 조리·숙성. 오리 컨핏, 마늘 컨핏 등',
    en: 'Slow-cooked and kept in oil or fat. Duck confit, garlic confit and more',
    ja: '油や脂で低温調理・熟成。鴨のコンフィ、ニンニクのコンフィなど',
    zh: '在油或脂肪中低温慢煮并熟成。油封鸭、油封蒜等',
  },
  fermentation: {
    ko: '누룩, 장, 김치 발효, 고지, 코지 활용',
    en: 'Built on nuruk, jang, kimchi fermentation and koji',
    ja: 'ヌルク・醤・キムチ発酵・麹を活用',
    zh: '运用韩式酒曲、酱、泡菜发酵与米曲',
  },
  curing: {
    ko: '소금·설탕·향신 기반 건조·습식 큐어링',
    en: 'Dry or wet curing with salt, sugar and aromatics',
    ja: '塩・砂糖・香辛料によるドライ・ウェットキュアリング',
    zh: '以盐、糖、香料进行干腌或湿腌',
  },
  smoking: {
    ko: '냉훈·온훈·우드 칩 종류별',
    en: 'Cold or hot smoke, by wood-chip type',
    ja: '冷燻・温燻、ウッドチップの種類別',
    zh: '冷熏·热熏，按木屑种类区分',
  },
  aging: {
    ko: '건식·습식 숙성, 치즈·육류·해산물 숙성',
    en: 'Dry and wet aging of cheese, meat and seafood',
    ja: 'ドライ・ウェット熟成。チーズ・肉・魚介の熟成',
    zh: '干式·湿式熟成，奶酪·肉类·海鲜熟成',
  },
  modernist: {
    ko: '젤화, 에멀전, 스페리피케이션, 액화질소 등',
    en: 'Gels, emulsions, spherification, liquid nitrogen and more',
    ja: 'ゲル化、エマルション、スフェリフィケーション、液体窒素など',
    zh: '凝胶化、乳化、晶球化、液氮等',
  },
}

/**
 * 기법 7개 (이름·프롬프트 hint·셰프용 설명). 메뉴 축 모든 대분류에 고르는 공통 꼬리표라 대분류 나무에 넣지 않았다.
 * 코스·테이스팅 흐름의 스타일·기법 단계도 이 목록을 그대로 가져다 쓴다. 저장값은 꼬리표와 같은 menuTags.technique
 */
export const MENU_TECHNIQUES: readonly MenuTechnique[] = MENU_TAGS.technique.map((tag) => ({
  ...tag,
  note: MENU_TECHNIQUE_NOTES[tag.id] ?? { ko: '', en: '', ja: '', zh: '' },
}))

/** 기법 셰프용 설명. 기법이 아니거나 모르는 id 면 undefined. ko·ja·zh 밖의 언어는 영어 */
export function menuTechniqueNote(id: string, language: string): string | undefined {
  const note = MENU_TECHNIQUE_NOTES[id]
  if (!note) return undefined
  return language === 'ko' || language === 'ja' || language === 'zh' ? note[language] : note.en
}
